import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/BackButton';
import { useAuth } from '@/lib/auth';
import { RaptorLogo } from '@/components/RaptorLogo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NotificationBell } from '@/components/NotificationBell';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SideNav, NavItem, useSidebarState } from '@/components/SideNav';
import { LayoutDashboard, Briefcase, LogOut, UserCircle2, ClipboardCheck } from 'lucide-react';

const Item: React.FC<{ to: string; icon: React.ReactNode; label: string; end?: boolean }> = ({ to, icon, label, end }) => (
  <NavItem to={to} end={end} label={label} icon={icon}
    activeClass="bg-raptor-blue/15 text-raptor-navy font-semibold border-l-2 border-raptor-blue"
    idleClass="text-raptor-navy/70 hover:bg-raptor-neutral/60" />
);

export const BrokerLayout: React.FC = () => {
  const { user, role, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async (): Promise<void> => { await logout(); nav('/login', { replace: true }); };
  const { collapsed, toggle } = useSidebarState();

  return (
    <div className="flex-1 flex bg-background overflow-hidden">
      <SideNav collapsed={collapsed} toggle={toggle}
        className="bg-white border-r border-border/70 shadow-sm"
        toggleClassName="bg-white border border-border/70 text-raptor-navy hover:bg-raptor-neutral/60">
        <div className="h-16 px-4 py-2 flex items-center justify-center border-b border-border/60 flex-shrink-0"><RaptorLogo size={40} slot="sidebar_logo" /></div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto min-h-0">
          <Item to="/broker" icon={<LayoutDashboard size={16} />} label="Dashboard" end />
          <Item to="/broker/clients" icon={<Briefcase size={16} />} label="Clients" />
          <Item to="/broker/surveys" icon={<ClipboardCheck size={16} />} label="Survey approvals" />
        </nav>
        <div className="p-3 border-t border-border/60 flex-shrink-0">
          <NavLink to="/broker/profile" title={collapsed ? user?.full_name : undefined}
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2'} p-2 rounded-md hover:bg-raptor-neutral/60`}>
            <UserCircle2 size={22} className="text-raptor-blue flex-shrink-0" />
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-raptor-navy truncate">{user?.full_name}</div>
                <div className="text-[11px] text-raptor-navy/60 truncate">Broker · {user?.broker_id}</div>
              </div>
            )}
          </NavLink>
          <Button variant="ghost" size="sm" className={`w-full mt-2 ${collapsed ? 'justify-center px-0' : 'justify-start'}`} onClick={doLogout} title={collapsed ? 'Sign out' : undefined}>
            <LogOut size={16} /> {!collapsed && 'Sign out'}
          </Button>
        </div>
      </SideNav>
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-border/70 h-16 px-6 flex items-center justify-between flex-shrink-0 z-10">
          <div className="flex items-center gap-3">
            <BackButton />
            <div>
              <h1 className="font-headline text-raptor-navy text-lg tracking-wide leading-tight">Broker Portal</h1>
              <p className="text-xs text-raptor-navy/60 font-body leading-tight">Scoped to your brokerage only</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="cyan">Broker</Badge>
            <ThemeToggle />
            <NotificationBell />
          </div>
        </header>
        <div className="flex-1 overflow-auto p-6"><Outlet /></div>
      </main>
    </div>
  );
};
